import { useState } from 'react';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { db } from '../lib/firebase';
import type { FlightDoc } from '../lib/types';
import { FlightTrackMap } from './FlightTrackMap';

const r1 = (n: number) => Math.round(n * 10) / 10;
const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : 0);

// Edit a logged flight — primarily to correct Hobbs/Tach readings (the source of
// hours). `flight.flightId` must be the Firestore doc id. Recomputes hoursFlown
// from Hobbs on save. Owner flights are never billed; for billed flights the server
// reconciles charges from the corrected hours.
export function FlightEditModal({ flight, onClose }: { flight: FlightDoc; onClose: () => void }) {
  const [f, setF] = useState<FlightDoc>({ ...flight });
  const setStr = (k: keyof FlightDoc, v: string) => setF((p) => ({ ...p, [k]: v }));
  const setN = (k: keyof FlightDoc, v: string) => setF((p) => ({ ...p, [k]: v === '' ? 0 : Number(v) }));
  const [busy, setBusy] = useState(false);

  const hobbsHours = r1(num(f.hobbsIn) - num(f.hobbsOut));
  const tachHours = r1(num(f.tachIn) - num(f.tachOut));
  const bad = num(f.hobbsIn) <= num(f.hobbsOut);

  async function save() {
    if (!db || bad) return;
    setBusy(true);
    await updateDoc(doc(db, 'flights', flight.flightId), {
      date: f.date,
      hobbsOut: num(f.hobbsOut),
      hobbsIn: num(f.hobbsIn),
      tachOut: num(f.tachOut),
      tachIn: num(f.tachIn),
      hoursFlown: hobbsHours,
      departureAirport: f.departureAirport ?? '',
      destinations: f.destinations ?? [],
      notes: f.notes ?? '',
      isCorrected: true,
    });
    onClose();
  }
  async function remove() {
    if (!db) return;
    if (!window.confirm('Delete this flight permanently? This cannot be undone.')) return;
    setBusy(true);
    await deleteDoc(doc(db, 'flights', flight.flightId));
    onClose();
  }

  const Field = ({ label, k, step }: { label: string; k: keyof FlightDoc; step?: string }) => (
    <div>
      <label className="label">{label}</label>
      <input className="input" type="number" inputMode="decimal" step={step ?? '0.1'} value={(f[k] as number) ?? ''} onChange={(e) => setN(k, e.target.value)} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-canvas">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 py-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <button onClick={onClose} className="flex items-center gap-1 text-sky-600"><ChevronLeft size={20} /> Back</button>
        <span className="font-display font-semibold text-navy-900">Edit flight</span>
        <button onClick={remove} disabled={busy} className="p-1.5 text-red-600"><Trash2 size={18} /></button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
        {f.track && f.track.length > 1 && (
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Flight track · ADS-B</span>
              {f.trackMaxAlt ? <span className="text-xs text-ink-muted">max {Math.round(f.trackMaxAlt).toLocaleString()} ft</span> : null}
            </div>
            <FlightTrackMap track={f.track} />
          </div>
        )}
        <div className="mb-4">
          <label className="label">Date</label>
          <input className="input" type="date" value={f.date ?? ''} onChange={(e) => setStr('date', e.target.value)} />
        </div>

        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Hobbs</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hobbs out" k="hobbsOut" />
          <Field label="Hobbs in" k="hobbsIn" />
        </div>
        <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${bad ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-ink'}`}>
          {bad ? 'Hobbs in must be greater than Hobbs out.' : <>Hobbs time: <strong>{hobbsHours.toFixed(1)} hrs</strong></>}
        </div>

        <div className="mt-5 mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Tach</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tach out" k="tachOut" />
          <Field label="Tach in" k="tachIn" />
        </div>
        <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-ink">Tach time: <strong>{tachHours.toFixed(1)} hrs</strong></div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <label className="label">From</label>
            <input className="input" value={f.departureAirport ?? ''} onChange={(e) => setStr('departureAirport', e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input className="input" value={(f.destinations ?? []).join(', ')} onChange={(e) => setF((p) => ({ ...p, destinations: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} />
          </div>
        </div>

        <div className="mt-5">
          <label className="label">Notes</label>
          <textarea className="input-textarea" value={f.notes ?? ''} onChange={(e) => setStr('notes', e.target.value)} />
        </div>

        {flight.billingMode && flight.billingMode !== 'owner' && (
          <p className="mt-4 text-xs text-amber-700">This is a billed flight — correcting the hours will re-reconcile its charge on the server.</p>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button onClick={save} disabled={busy || bad} className="btn-primary w-full">{busy ? 'Saving…' : 'Save flight'}</button>
      </div>
    </div>
  );
}
